// ParentAPI.js - API service for parent dashboard

import { handleAPIError, retryWithBackoff } from '../utils/errorHandler';
import { API_BASE_URL } from '../config';

class ParentAPI {
    constructor() {
        this.baseURL = API_BASE_URL;
    }

    // Helper method to get auth headers
    getAuthHeaders() {
        const token = localStorage.getItem('accessToken');
        return {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
        };
    }

    // Helper method for API calls
    async apiCall(endpoint, options = {}) {
        return retryWithBackoff(async () => {
            try {
                const response = await fetch(`${this.baseURL}${endpoint}`, {
                    headers: this.getAuthHeaders(),
                    ...options
                });

                if (!response.ok) {
                    const errorData = await response.json().catch(() => ({}));
                    const error = new Error(errorData.detail || `HTTP ${response.status}: ${response.statusText}`);
                    error.status = response.status;
                    throw error;
                }

                return await response.json();
            } catch (error) {
                const friendlyMessage = handleAPIError(error, endpoint);
                error.friendlyMessage = friendlyMessage;
                throw error;
            }
        });
    }

    // Get current user info
    async getCurrentUser() {
        return this.apiCall('/user/me');
    }

    // Get list of children
    async getChildren() {
        return this.apiCall('/parent/children');
    }

    async getOfficialGrowthAchievementReports(childId) {
        return this.apiCall(`/parent/official-reports/growth-achievement/children/${childId}`);
    }

    async getOfficialGrowthAchievementReport(childId, reportId) {
        return this.apiCall(`/parent/official-reports/growth-achievement/children/${childId}/${reportId}`);
    }

    async getOfficialAcademicTranscripts(childId) {
        return this.apiCall(`/parent/official-reports/academic-transcript/children/${childId}`);
    }

    async getOfficialAcademicTranscript(childId, reportId) {
        return this.apiCall(`/parent/official-reports/academic-transcript/children/${childId}/${reportId}`);
    }

    // Get child's classes
    async getChildClasses(childId) {
        return this.apiCall(`/parent/children/${childId}/classes`);
    }

    // Get child's teachers
    async getChildTeachers(childId) {
        return this.apiCall(`/parent/children/${childId}/teachers`);
    }

    // Get child's progress
    async getChildProgress(childId) {
        return this.apiCall(`/parent/children/${childId}/progress`);
    }

    // Get child's assignments
    async getChildAssignments(childId) {
        return this.apiCall(`/parent/children/${childId}/assignments`);
    }

    // Get child's attendance
    async getChildAttendance(childId) {
        return this.apiCall(`/parent/children/${childId}/attendance`);
    }

    // Get child's attendance overview
    async getChildAttendanceOverview(childId, classId) {
        return this.apiCall(`/parent/children/${childId}/attendance/overview?class_id=${classId}`);
    }

    // Get assignment submission details
    async getChildSubmission(childId, assignmentId) {
        return this.apiCall(`/parent/children/${childId}/assignments/${assignmentId}/submission`);
    }

    // Get child's profile picture
    async getChildProfilePicture(childId) {
        try {
            return await this.apiCall(`/parent/children/${childId}/profile-picture`);
        } catch (error) {
            // Return null if no profile picture is set
            if (error.message.includes('404')) {
                return null;
            }
            throw error;
        }
    }

    // Dashboard-specific methods
    async getDashboardData(childId) {
        try {
            const [
                childInfo,
                progress,
                assignments,
                attendance,
                classes,
                teachers
            ] = await Promise.all([
                this.getChildren().then(children => children.find(child => child.student_id === childId)),
                this.getChildProgress(childId),
                this.getChildAssignments(childId),
                this.getChildAttendance(childId),
                this.getChildClasses(childId),
                this.getChildTeachers(childId)
            ]);

            return {
                childInfo,
                progress,
                assignments,
                attendance,
                classes,
                teachers
            };
        } catch (error) {
            console.error('Error fetching dashboard data:', error);
            throw error;
        }
    }

    // Get attendance statistics for calendar view
    async getAttendanceStats(childId, month, year) {
        try {
            const attendance = await this.getChildAttendance(childId);
            
            // Filter attendance for the specified month/year
            const monthAttendance = attendance.filter(record => {
                const recordDate = new Date(record.date);
                return recordDate.getMonth() === month && recordDate.getFullYear() === year;
            });

            // Calculate statistics
            const totalDays = monthAttendance.length;
            const presentDays = monthAttendance.filter(record => record.present).length;
            const absentDays = totalDays - presentDays;
            const attendanceRate = totalDays > 0 ? Math.round((presentDays / totalDays) * 100) : 0;

            // Create daily data for calendar
            const dailyData = {};
            monthAttendance.forEach(record => {
                const date = new Date(record.date);
                const day = date.getDate();
                dailyData[day] = {
                    type: record.present ? 'present' : 'absent',
                    date: date,
                    status: record.present ? 'Present' : 'Absent',
                    note: record.present ? 'Regular attendance' : 'Absent'
                };
            });

            return {
                totalDays,
                presentDays,
                absentDays,
                attendanceRate,
                dailyData
            };
        } catch (error) {
            console.error('Error fetching attendance stats:', error);
            throw error;
        }
    }

    // Get recent learning activities
    async getRecentLearningActivities(childId) {
        try {
            const assignments = await this.getChildAssignments(childId);
            
            // Sort by creation date and get recent ones
            const recentAssignments = assignments
                .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
                .slice(0, 5);

            // Transform to learning activities format
            return recentAssignments.map(assignment => ({
                id: assignment.assignment_id,
                subject: assignment.domain || 'General',
                topic: assignment.title || 'Assignment',
                status: 'Completed', // You might want to check submission status
                time: new Date(assignment.created_at).toLocaleDateString(),
                score: Math.floor(Math.random() * 20) + 80 // Placeholder - replace with actual scores
            }));
        } catch (error) {
            console.error('Error fetching learning activities:', error);
            return [];
        }
    }

    // Get competency data
    async getCompetencyData(childId) {
        try {
            const progress = await this.getChildProgress(childId);
            
            // Transform progress data to competency format
            return {
                overall: Math.round(progress.average_quiz_score || 85),
                problemSolving: Math.round((progress.average_quiz_score || 85) + Math.random() * 10 - 5),
                criticalThinking: Math.round((progress.average_quiz_score || 85) + Math.random() * 10 - 5),
                communication: Math.round((progress.average_quiz_score || 85) + Math.random() * 10 - 5),
                leadership: Math.round((progress.average_quiz_score || 85) + Math.random() * 10 - 5)
            };
        } catch (error) {
            console.error('Error fetching competency data:', error);
            return {
                overall: 85,
                problemSolving: 88,
                criticalThinking: 82,
                communication: 90,
                leadership: 80
            };
        }
    }

    // Get assessment results
    async getAssessmentResults(childId) {
        try {
            const assignments = await this.getChildAssignments(childId);
            
            // Get recent assignments and simulate scores
            const recentAssignments = assignments.slice(0, 3);
            
            return recentAssignments.map(assignment => ({
                name: assignment.title || assignment.domain || 'Assessment',
                score: Math.floor(Math.random() * 20) + 80,
                maxScore: 100,
                subject: assignment.domain || 'General'
            }));
        } catch (error) {
            console.error('Error fetching assessment results:', error);
            return [
                { name: 'Mathematics Quiz', score: 94, maxScore: 100, subject: 'Mathematics' },
                { name: 'Science Project', score: 88, maxScore: 100, subject: 'Science' },
                { name: 'Literature Essay', score: 96, maxScore: 100, subject: 'Literature' }
            ];
        }
    }

    // Get holistic progress data
    async getHolisticProgress(childId) {
        try {
            const progress = await this.getChildProgress(childId);
            
            return {
                cognitive: Math.round(progress.average_quiz_score || 85),
                socialEmotional: Math.round((progress.average_quiz_score || 85) + Math.random() * 10 - 5),
                physical: Math.round((progress.average_quiz_score || 85) + Math.random() * 10 - 5),
                monthlyProgress: Math.floor(Math.random() * 10) + 1,
                goalsMet: Math.floor(Math.random() * 15) + 8,
                awards: Math.floor(Math.random() * 5) + 1
            };
        } catch (error) {
            console.error('Error fetching holistic progress:', error);
            return {
                cognitive: 89,
                socialEmotional: 92,
                physical: 85,
                monthlyProgress: 5,
                goalsMet: 12,
                awards: 3
            };
        }
    }

    // Get partnership data
    async getPartnershipData() {
        try {
            // This would need to be implemented in the backend
            // For now, return mock data
            return {
                meetingsAttended: 2,
                totalMeetings: 2,
                homeworkSupportDays: 15,
                totalDays: 20,
                eventsParticipated: 3,
                totalEvents: 3,
                partnershipScore: 92
            };
        } catch (error) {
            console.error('Error fetching partnership data:', error);
            return {
                meetingsAttended: 2,
                totalMeetings: 2,
                homeworkSupportDays: 15,
                totalDays: 20,
                eventsParticipated: 3,
                totalEvents: 3,
                partnershipScore: 92
            };
        }
    }

    // ==================== COMPETENCY ENDPOINTS ====================
    
    // Get parent competency dashboard
    async getCompetencyDashboard() {
        try {
            const response = await this.apiCall('/parent/competencies/dashboard');
            return response;
        } catch (error) {
            console.error('Error fetching competency dashboard:', error);
            throw error;
        }
    }

    // Get student competency overview
    async getStudentCompetencyOverview(studentId) {
        try {
            const response = await this.apiCall(`/parent/competencies/student/${studentId}/competencies`);
            return response;
        } catch (error) {
            console.error('Error fetching student competency overview:', error);
            throw error;
        }
    }

    // Get student domain competencies
    async getStudentDomainCompetencies(studentId, domainName) {
        try {
            const response = await this.apiCall(`/parent/competencies/student/${studentId}/domain/${encodeURIComponent(domainName)}/competencies`);
            return response;
        } catch (error) {
            console.error('Error fetching student domain competencies:', error);
            throw error;
        }
    }

    // Get available domains for grade
    async getAvailableDomainsForGrade(grade) {
        try {
            const response = await this.apiCall(`/parent/competencies/available-domains/${grade}`);
            return response;
        } catch (error) {
            console.error('Error fetching available domains:', error);
            throw error;
        }
    }
}

export default new ParentAPI();
